/**
 * Lazy import wrapper with automatic one-time reload on chunk load failure.
 *
 * After a deployment, users with stale tabs may try to load JS chunks that
 * no longer exist (filenames include content hashes). The browser throws:
 * - "Importing a module script failed" (Safari)
 * - "Failed to fetch dynamically imported module" (Chrome)
 * - "Loading chunk X failed" (Webpack, less common with Vite)
 *
 * This wrapper catches the import error and triggers a single page reload
 * to pick up the new index.html (which references the new chunk filenames).
 * A sessionStorage flag prevents infinite reload loops.
 *
 * Telemetry events (captured by logger → Sentry breadcrumbs):
 * - chunk_reload_triggered: reload initiated after chunk load failure
 * - chunk_reload_recovered: import succeeded after a previous reload
 * - chunk_reload_failed: import failed even after reload (user sees error boundary)
 */

import { lazy } from 'react';
import type { ComponentType } from 'react';
import { logger } from '@/shared/utils/logger';

// Declared in vite.config.mts define block
declare const __APP_VERSION__: string;

const RELOAD_FLAG_PREFIX = 'chunk-reload:';
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';

/**
 * Checks if an error is a chunk/module load failure.
 */
const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('importing a module script failed') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('unable to preload css')
  );
};

/**
 * Wraps a dynamic import with automatic one-time reload on chunk load failure.
 *
 * Usage (drop-in replacement for React.lazy):
 * ```ts
 * const MyPage = lazyWithReload(() => import('./MyPage'));
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithReload = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> => {
  return lazy(async () => {
    const key = `${RELOAD_FLAG_PREFIX}${window.location.pathname}`;
    const isPostReload = sessionStorage.getItem(key) === '1';

    try {
      const module = await importFn();

      // If we got here after a reload, the recovery worked
      if (isPostReload) {
        sessionStorage.removeItem(key);
        logger.info('[chunk_reload_recovered]', window.location.pathname, `v${appVersion}`);
      }

      return module;
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      if (isPostReload) {
        // Already reloaded once for this path — don't loop.
        // Clear the flag so future deploys can trigger another reload.
        sessionStorage.removeItem(key);
        logger.error(
          '[chunk_reload_failed] Chunk load failed after reload — showing error boundary',
          error,
          { path: window.location.pathname, version: appVersion },
        );
        throw error;
      }

      // Set flag and reload
      logger.info(
        '[chunk_reload_triggered] Chunk load failed — reloading page to fetch updated assets',
        window.location.pathname,
        `v${appVersion}`,
        error instanceof Error ? error.message : String(error),
      );
      sessionStorage.setItem(key, '1');
      window.location.reload();

      // Return a never-resolving promise to prevent React from rendering
      // while the page is reloading
      return new Promise(() => { /* never resolves — page is reloading */ });
    }
  });
};
